import React, { SyntheticEvent } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Box, Dialog, Grid2, IconButton, Paper, Typography } from "@mui/material";
import { faXmark } from "@fortawesome/sharp-regular-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import { pageHeaderHeight } from "../../styles/theme";
import { LoanNumber } from "./internals/components/LoanNumber";
import { PlatformBase } from "../PlatformBase/PlatformBase";

library.add(faXmark);

interface PageDialogProps {
  title: string;
  eventName: string;
  showPageHeader: boolean;
  showCloseIcon: boolean;
  children: React.ReactNode;
  keepMounted?: boolean;
  onOpen?: () => void;
  loanNumber?: string;
  ariaLabel?: string;
}

export const PlatformPageDialog = ({ keepMounted = false, ...props }: PageDialogProps) => {
  // TODO: I am a one off that cannot use the core global
  const LAYOUT_TOGGLE_STORAGE_KEY = "ENABLE_ALTERNATE_LAYOUT";
  const useAlternateLayout = localStorage.getItem(LAYOUT_TOGGLE_STORAGE_KEY) === "true";

  const [open, setOpen] = React.useState(false);
  const [shouldKeepMounted, setShouldKeepMounted] = React.useState(keepMounted);
  const gridWidth = { xs: 500, sm: 570, md: 760, lg: 970, xl: 1032 };

  const handleClose = (event: SyntheticEvent) => {
    onClose();
    event.preventDefault();
  };

  React.useEffect(() => {
    window.addEventListener("open" + props.eventName, onOpen);
    window.addEventListener("close" + props.eventName, onClose);
    return () => {
      window.removeEventListener("open" + props.eventName, onOpen);
      window.removeEventListener("close" + props.eventName, onClose);
    };
  }, [keepMounted, props.eventName]);

  const onOpen = () => {
    setOpen(true);
    if (props.onOpen) {
      props.onOpen();
    }
    if (keepMounted) {
      setShouldKeepMounted(true);
    }
  };

  const onClose = () => {
    setOpen(false);
    window.dispatchEvent(new Event("closeEventFrom" + props.eventName));
    window.dispatchEvent(new Event("focusToChatInput"));
  };

  function getHeight() {
    if (useAlternateLayout) {
      return "100%";
    } else {
      return props.showPageHeader ? `calc(100% - ${pageHeaderHeight}px)` : "100%";
    }
  }

  function getTopMargin() {
    if (useAlternateLayout) {
      return undefined;
    }
    return props.showPageHeader ? `${pageHeaderHeight}px` : null;
  }

  function getContainer() {
    if (useAlternateLayout) {
      return document.getElementById("single-spa-application:assistant") || document.body;
    }
    return document.getElementById("single-spa-main-content") || document.body;
  }

  return (
    <PlatformBase>
      <Dialog
        container={() => getContainer()}
        keepMounted={shouldKeepMounted}
        open={open}
        fullScreen
        hideBackdrop
        disableEnforceFocus
        slotProps={{
          paper: {
            elevation: 0,
          },
        }}
        aria-label={props.ariaLabel ? props.ariaLabel + "-dialog" : undefined}
        sx={{
          position: "absolute",
          marginTop: getTopMargin(),
          height: getHeight(),
        }}
      >
        <Paper
          elevation={0}
          sx={{
            overflowY: "auto",
            overflowX: "hidden",
            height: "100%",
          }}
        >
          {props.showCloseIcon && (
            <IconButton
              type={"button"}
              onClick={(event) => handleClose(event)}
              data-testid={"close-" + props.eventName + "-icon-button"}
              aria-label={"close-" + props.eventName + "-icon-button"}
              sx={{
                position: "absolute",
                top: "8px",
                right: "8px",
                height: "59px",
                width: "59px",
              }}
            >
              <FontAwesomeIcon icon={faXmark} size={"xl"} data-testid={"CloseIcon"} />
            </IconButton>
          )}
          <Box height={"100%"} display={"grid"} gridTemplateRows={"min-content minmax(0, 1fr)"}>
            <Box width={"100%"} display={"flex"} flexDirection={"column"} alignItems={"center"}>
              <Grid2 container width={gridWidth}>
                <Grid2 size="grow" justifyContent="left">
                  <Typography variant="h1" align="left" data-testid={"title"}>
                    {props.title}
                  </Typography>
                </Grid2>
                <Grid2 size="auto" justifyContent="left">
                  <LoanNumber loanNumber={props.loanNumber}></LoanNumber>
                </Grid2>
              </Grid2>
            </Box>
            <Box width={"100%"} display={"flex"} flexDirection={"column"} alignItems={"center"}>
              <Box width={gridWidth} height={"100%"}>
                {props.children}
              </Box>
            </Box>
          </Box>
        </Paper>
      </Dialog>
    </PlatformBase>
  );
};
